// src/components/NutritionPlanLoader.tsx
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Apple, ChefHat, Utensils } from "lucide-react";

interface NutritionPlanLoaderProps {
  onComplete?: () => void;
}

export default function NutritionPlanLoader({ onComplete }: NutritionPlanLoaderProps) {
  const [progress, setProgress] = useState(0);
  const [activeSection, setActiveSection] = useState(0);

  const features = [
    {
      title: "Analyzing Your Nutritional Needs",
      subtitle: "Calculating optimal macros",
      icon: Apple,
    },
    {
      title: "Crafting Personalized Recipes",
      subtitle: "Based on your preferences",
      icon: ChefHat,
    },
    {
      title: "Building Your Meal Plan",
      subtitle: "Delicious and balanced meals",
      icon: Utensils,
    }
  ];

  useEffect(() => {
    // Slower progress: 30 seconds total
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return 95; // Stop at 95% until complete
        }
        return prev + 1;
      });
    }, 300);

    // Feature rotation: 5 seconds per feature
    const featureInterval = setInterval(() => {
      setActiveSection((prev) => (prev + 1) % features.length);
    }, 5000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(featureInterval);
    };
  }, []);

  // Complete when progress hits 95%
  useEffect(() => {
    if (progress === 95 && onComplete) {
      setTimeout(onComplete, 500);
    }
  }, [progress, onComplete]);

  const currentFeature = features[activeSection];
  const Icon = currentFeature.icon;

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-between p-6 md:p-12 overflow-hidden">
      
      {/* Floating food emojis in background */}
      <motion.div
        className="absolute top-20 left-20 text-6xl opacity-10"
        animate={{
          y: [0, -20, 0],
          rotate: [0, 10, 0],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        🥗
      </motion.div>

      <motion.div
        className="absolute top-40 right-32 text-5xl opacity-10"
        animate={{
          y: [0, 20, 0],
          rotate: [0, -10, 0],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.5,
        }}
      >
        🍳
      </motion.div>

      <motion.div
        className="absolute bottom-32 left-40 text-6xl opacity-10"
        animate={{
          y: [0, -15, 0],
          rotate: [0, 15, 0],
        }}
        transition={{
          duration: 4.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1,
        }}
      >
        🥑
      </motion.div>

      <motion.div
        className="absolute bottom-20 right-20 text-5xl opacity-10"
        animate={{
          y: [0, 25, 0],
          rotate: [0, -15, 0],
        }}
        transition={{
          duration: 5.5,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.5,
        }}
      >
        🍎
      </motion.div>

      {/* Additional floating food items */}
      <motion.div
        className="absolute top-1/3 left-1/4 text-4xl opacity-10"
        animate={{
          y: [0, 15, 0],
          rotate: [0, -8, 0],
        }}
        transition={{
          duration: 4.2,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 0.8,
        }}
      >
        🥦
      </motion.div>

      <motion.div
        className="absolute top-2/3 right-1/4 text-5xl opacity-10"
        animate={{
          y: [0, -18, 0],
          rotate: [0, 12, 0],
        }}
        transition={{
          duration: 4.8,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 1.2,
        }}
      >
        🍗
      </motion.div>

      {/* Logo */}
      <div className="w-full flex justify-center pt-4">
        <div className="relative">
          <img 
            src="https://usbamprqshmoalvlkjch.supabase.co/storage/v1/object/public/branding/Inputperformanceaiwhite.png" 
            alt="Input" 
            className="h-12 md:h-16 w-auto"
          />
          <div className="absolute inset-0 -m-2 flex items-center justify-center">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-green-400/20 animate-spin-slow" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center w-full max-w-2xl px-4">
        <div className="w-full flex flex-col items-center space-y-8 md:space-y-12">
          
          {/* Large Icon with nutrition theme */}
          <div className="relative">
            {features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <div
                  key={index}
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-1000 ${
                    index === activeSection 
                      ? 'opacity-100 scale-100 rotate-0' 
                      : 'opacity-0 scale-50 rotate-45'
                  }`}
                >
                  <FeatureIcon 
                    className="w-32 h-32 md:w-48 md:h-48 text-green-400 drop-shadow-[0_0_30px_rgba(167,139,250,0.6)]" 
                    strokeWidth={1.5}
                  />
                </div>
              );
            })}
            
            <div className="w-32 h-32 md:w-48 md:h-48" />
            
            {/* Green glow for nutrition theme */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-40 h-40 md:w-56 md:h-56 bg-green-400/10 rounded-full blur-3xl animate-pulse" />
            </div>
          </div>

          {/* Text Content */}
          <div className="text-center space-y-3 md:space-y-4 animate-fade-in">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="h-px w-8 md:w-12 bg-gradient-to-r from-transparent to-green-400" />
              <span className="text-green-400 font-semibold text-sm md:text-base">
                Step {activeSection + 1} of {features.length}
              </span>
              <div className="h-px w-8 md:w-12 bg-gradient-to-l from-transparent to-green-400" />
            </div>
            
            <h2 className="text-3xl md:text-5xl font-bold text-[#0f172a] leading-tight px-4">
              {currentFeature.title}
            </h2>
            
            <p className="text-lg md:text-2xl text-gray-500 font-light">
              {currentFeature.subtitle}
            </p>
          </div>

          {/* Feature Pills */}
          <div className="flex justify-center gap-3 md:gap-4">
            {features.map((feature, index) => {
              const FeatureIcon = feature.icon;
              return (
                <div
                  key={index}
                  className={`flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full transition-all duration-500 ${
                    index === activeSection
                      ? 'bg-green-400 scale-110'
                      : index < activeSection
                        ? 'bg-green-400/30'
                        : 'bg-black/5'
                  }`}
                >
                  <FeatureIcon 
                    className={`w-5 h-5 md:w-6 md:h-6 transition-colors ${
                      index === activeSection
                        ? 'text-[#0f172a]'
                        : index < activeSection
                          ? 'text-green-400'
                          : 'text-gray-600'
                    }`}
                    strokeWidth={2}
                  />
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* Bottom Progress */}
      <div className="w-full max-w-md space-y-4">
        <div className="w-full">
          <div className="h-1.5 bg-black/5 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-300 ease-out relative overflow-hidden"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/30 to-transparent animate-shimmer" />
            </div>
          </div>
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-gray-600">Loading your meal plan...</p>
            <p className="text-sm text-gray-500 font-medium">{progress}%</p>
          </div>
        </div>

        <div className="flex justify-center gap-2">
          {features.map((_, index) => (
            <div
              key={index}
              className={`h-1 rounded-full transition-all duration-500 ${
                index === activeSection 
                  ? 'w-12 bg-green-400 shadow-[0_0_10px_rgba(167,139,250,0.5)]' 
                  : index < activeSection 
                    ? 'w-6 bg-green-400/50' 
                    : 'w-6 bg-black/10'
              }`}
            />
          ))}
        </div>

        <p className="text-center text-sm text-gray-600 mt-4">
          <span className="font-semibold text-[#0f172a]">Input</span>
        </p>
      </div>
    </div>
  );
}
